import { useCallback, useState } from 'react';
import { Button, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { FilterFunctionaries } from './FilterFunctionaries';
import { useFunctionariesList } from './useFunctionariesList';
import { FunctionariesList } from './FunctionariesList';
import {
	ADD_FUNCTIONARY,
	EDIT_FUNCTIONARY,
	SELECT_MODULE,
} from '../../../shared/Routes';
import { useBatchId } from '../../../hooks/useBatchId';
import { UserRole } from '../../../shared/VolunteerApplicationServiceTypes';
import { useAddOrEditFunctionaryPage } from '../AddOrEditFunctionaryPage/useAddOrEditFunctionaryPage';
import { UndoOutlined } from '@ant-design/icons';
import Icon from '../../../components/ui-components/Icon';
import LeftArrowWhite from '../../../assets/images/arrow-left-white.svg';
import LeftArrowBlack from '../../../assets/images/arrow-left.svg';
import FilterIcon from '../../../assets/images/filter-icon.svg';
import { BulkUploadFunctionaries } from './BulkUploadFunctionaries';
import useUserSession from '../../../hooks/useUserSession';
import { hasPrivilege } from '../../../shared/Utils';
import { PrivilegeCodes } from '../../../shared/RoleConstants';
import { NoAccess } from '../../../components/NoAccess';
import { PositionAssignmentUser } from '../../../shared/MySrcm';

const { Text } = Typography;

export const FunctionariesListContainer = () => {
	const {
		functionaryList,
		onPageChange,
		filter,
		onFilterChange,
		paging,
		navigateWithFunctionarySrcmId,
		isFilterTextApplied,
		loading,
		isApiFetched,
		setIsFilterTextApplied,
	} = useFunctionariesList();
	const { t } = useTranslation();
	const { navigateToRoute } = useBatchId();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const [isFilterPopup, setIsFilterPopup] = useState<boolean>(false);
	const [isBulkUploadPopup, setBulkUploadPopup] = useState<boolean>(false);
	const [resetFilterValues, setResetFilterValues] = useState<boolean>(false);
	const displayFunctionariesLink = selectedRole === UserRole.SNP_ADMIN;
	const isApprover = selectedRole === UserRole.APPROVER;

	const { addFunctionaryStatus } = useAddOrEditFunctionaryPage();

	const handleEditProposerClick = useCallback(
		(functionary: PositionAssignmentUser) => {
			navigateWithFunctionarySrcmId(EDIT_FUNCTIONARY, functionary.ref);
		},
		[navigateWithFunctionarySrcmId]
	);

	const handleBackClick = useCallback(() => {
		navigateToRoute(SELECT_MODULE);
	}, [navigateToRoute]);

	const handleFilterPopup = useCallback(() => {
		setIsFilterPopup(true);
	}, []);

	const onClose = useCallback(() => {
		setIsFilterPopup(false);
	}, []);

	const showBulkUpload = hasPrivilege(
		PrivilegeCodes.BULK_UPLOAD_FUNCTIONARIES,
		loggedInUserPrivileges
	);

	const handleBulkUploadPopup = useCallback(() => {
		setBulkUploadPopup(true);
	}, []);

	const onCloseBulkUploadPopup = useCallback(() => {
		setBulkUploadPopup(false);
	}, []);

	const resetFunctionaryFilters = useCallback(() => {
		void onFilterChange({});
		setResetFilterValues(true);
		setIsFilterTextApplied(false);
	}, [onFilterChange, setIsFilterTextApplied]);

	const showAddFunctionary = hasPrivilege(
		PrivilegeCodes.CREATE_OR_UPDATE_POSITION_ASSIGNMENT,
		loggedInUserPrivileges
	);

	const showDownloadTemplate = hasPrivilege(
		PrivilegeCodes.DOWNLOAD_TEMPLATE_FOR_BULK_UPLOAD_FUNCTIONARIES,
		loggedInUserPrivileges
	);

	const showRegisterFunctionary = hasPrivilege(
		PrivilegeCodes.REGISTER_FUNCTIONARY,
		loggedInUserPrivileges
	);

	const showNoAccess =
		showAddFunctionary ||
		showBulkUpload ||
		showDownloadTemplate ||
		showRegisterFunctionary;

	const handleAddProposerClick = useCallback(() => {
		addFunctionaryStatus();
		resetFunctionaryFilters();
		navigateToRoute(ADD_FUNCTIONARY);
	}, [navigateToRoute, resetFunctionaryFilters, addFunctionaryStatus]);

	if (!showNoAccess) {
		return <NoAccess />;
	}

	return (
		<div className="flex flex-col min-w-[350px] max-w-[480px] sm:min-w-0 sm:max-w-none px-[16px] py-[8px]">
			<Text className="font-sans font-bold text-xl text-secondary-black mb-4">
				{isApprover ? t('listOfProposers') : t('listOfFunctionaries')}
			</Text>

			<div className="flex justify-between">
				<div className="sm:mt-0 md:mt-[16px] flex gap-4">
					{/* Back Button - Web */}
					<Button
						type="primary"
						onClick={handleBackClick}
						className="hidden md:block font-bold text-sm"
					>
						<span className="flex gap-2">
							<Icon src={LeftArrowWhite} width="w-4" height="h-4" />
							{t('back')}
						</span>
					</Button>

					{/* Back Button - Mobile */}
					<div className="block sm:hidden">
						<a
							onClick={handleBackClick}
							className="relative top-[8px] md:top-[3px] mr-2 font-bold text-sm"
						>
							<img src={LeftArrowBlack} width={'w-4'} height={'h-4'} />
						</a>
					</div>
					{/* Filter Button - web */}
					<Button
						className={`hidden sm:block text-primary-blue font-sans font-bold text-sm border border-solid ${
							isFilterTextApplied ? 'border-primary-blue' : 'border-gray-200'
						}`}
						variant="outlined"
						onClick={handleFilterPopup}
					>
						{isFilterTextApplied ? t('filterApplied') : t('filter')}
					</Button>

					{/* Reset Filter Button - Web */}
					{isFilterTextApplied ? (
						<Button
							className="hidden sm:block h-9 text-red-500 font-sans font-bold"
							type="link"
							onClick={resetFunctionaryFilters}
						>
							<span>
								{t('resetFilters')}
								<UndoOutlined className="ml-2" />
							</span>
						</Button>
					) : (
						''
					)}
				</div>
				<div className="sm:mt-0 md:mt-[16px] flex gap-4 sm:justify-start md:justify-end">
					{showBulkUpload && (
						<Button
							type="primary"
							onClick={handleBulkUploadPopup}
							className="hidden md:block font-bold text-sm mt-0.5"
						>
							{t('bulkUpload')}
						</Button>
					)}
					{/* Filter Button - mobile */}
					<Button
						className="block sm:hidden"
						variant="outlined"
						onClick={handleFilterPopup}
					>
						<img src={FilterIcon} alt="Filter Icon" className="w-5 h-5" />
					</Button>
					{/* Add Proposer/Add Functionary - Web */}
					{showAddFunctionary && (
						<Button
							type="link"
							onClick={handleAddProposerClick}
							className={`hidden md:block text-primary-blue font-sans font-bold text-sm border border-solid mt-0.5 border-primary-blue`}
							variant="outlined"
						>
							{displayFunctionariesLink
								? t('addFunctionary')
								: t('addProposer')}
						</Button>
					)}
				</div>
			</div>
			{/* Add Proposer/Add Functionary - Mobile */}
			<Button
				onClick={handleAddProposerClick}
				className="w-full block sm:hidden text-primary-blue font-sans font-bold text-sm border border-solid border-primary-blue mt-[16px]"
			>
				{displayFunctionariesLink ? t('addFunctionary') : t('addProposer')}
			</Button>
			<div className="mb-[16px]">
				<FilterFunctionaries
					open={isFilterPopup}
					value={filter}
					onClose={onClose}
					onFilterChange={(filters) => {
						void onFilterChange(filters);
					}}
					resetFilterValues={resetFilterValues}
					setResetFilterValues={setResetFilterValues}
				/>
			</div>
			{isBulkUploadPopup && (
				<div className="mb-[16px]">
					<BulkUploadFunctionaries
						open={isBulkUploadPopup}
						onClose={onCloseBulkUploadPopup}
					/>
				</div>
			)}
			<FunctionariesList
				data={functionaryList}
				loading={loading}
				isApiFetched={isApiFetched}
				onPageChange={onPageChange}
				onEditClick={handleEditProposerClick}
				pageConfig={paging}
				navigateWithFunctionarySrcmId={navigateWithFunctionarySrcmId}
				onFilterChange={(filters) => {
					void onFilterChange(filters);
				}}
			/>
		</div>
	);
};
